import { Link } from 'react-router-dom';
import {
  Notification,
  getNotificationStyle,
  getNotificationLink,
  formatRelativeTime
} from '../../hooks/useNotifications';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  showFullMessage?: boolean;
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  showFullMessage = false
}: NotificationItemProps) {
  const { color, icon } = getNotificationStyle(notification.type);
  const link = getNotificationLink(notification.type, notification.data);
  const isUnread = !notification.readAt;

  const handleClick = () => {
    if (isUnread && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  const content = (
    <div
      className={`flex gap-3 p-3 rounded-lg transition-colors ${
        isUnread
          ? 'bg-gold/5 hover:bg-gold/10'
          : 'hover:bg-surface'
      }`}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUnread ? 'bg-gold/20' : 'bg-surface'
      }`}>
        <span className={`text-sm ${color}`}>{icon}</span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium ${isUnread ? 'text-cream' : 'text-text-secondary'}`}>
            {notification.title}
          </p>
          {isUnread && (
            <span className="flex-shrink-0 w-2 h-2 bg-gold rounded-full mt-1.5" />
          )}
        </div>
        <p className={`text-sm mt-0.5 ${
          showFullMessage ? '' : 'line-clamp-2'
        } ${isUnread ? 'text-text-secondary' : 'text-text-muted'}`}>
          {notification.message}
        </p>
        <p className="text-xs text-text-muted mt-1">
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} onClick={handleClick} className="block">
        {content}
      </Link>
    );
  }

  return (
    <div onClick={handleClick} className={onMarkAsRead ? 'cursor-pointer' : ''}>
      {content}
    </div>
  );
}
